import {
  useInfiniteQuery,
  useMutation,
  useQuery,
  useQueryClient,
} from 'react-query';

import { useServiceError } from '@/hooks';

import {
  CreateQuotationItemRequest,
  CreateQuotationSupplierRequest,
  QuotationsQuery,
  ReportQuotationItemsSuppliersRequest,
  ReportQuotationType,
  SendQuotationRequest,
  UpdateQuotationItemRequest,
} from './Quotations.types';

import {
  listQuotations,
  deleteQuotation,
  createQuotation,
  updateQuotation,
  getQuotation,
  listQuotationItems,
  deleteQuotationItem,
  createQuotationItem,
  updateQuotationItem,
  listQuotationSuppliers,
  createQuotationSupplier,
  deleteQuotationSupplier,
  sendQuotation,
  reportQuotationItemsBySyppliers,
  reportQuotationItemsByQuotation,
  reportQuotationSupplierWinners,
} from './Quotations.service';

import { DEFAULT_ORDER, DEFAULT_ROWS } from '..';
import { notification } from 'antd';
import { useIntl } from 'react-intl';
import notificationError from '@/utils/functions/notificationError';

export function useQuotations(params?: QuotationsQuery) {
  const query = useInfiniteQuery(
    ['quotations', { ...params }],
    ({ pageParam }) => {
      const req: QuotationsQuery = {
        nextToken: pageParam,
        order: params?.order || DEFAULT_ORDER,
        rows: params?.rows || DEFAULT_ROWS,
        ...params,
      };

      return listQuotations(req);
    },
    {
      getNextPageParam: (lastPage) => lastPage.result?.nextToken,
    },
  );

  useServiceError(query.error);

  return query;
}

export function useQuotation(id: number) {
  const query = useQuery(['quotations', id], () => getQuotation(id));

  useServiceError(query.error);

  return query;
}

export function useCreateQuotation() {
  const mutation = useMutation(createQuotation);

  useServiceError(mutation.error);

  return mutation;
}

export function useUpdateQuotation() {
  const intl = useIntl();
  const queryClient = useQueryClient();
  const mutation = useMutation(updateQuotation, {
    onSuccess(_, data) {
      notification.success({
        message: intl.formatMessage({
          id: 'quotations.update.notification.success',
        }),
        description: intl.formatMessage({
          id: 'quotations.update.notification.successDescription',
        }),
      });

      queryClient.invalidateQueries(['quotations', data.id]);
    },
  });

  useServiceError(mutation.error);

  return mutation;
}

export function useDeleteQuotation() {
  const intl = useIntl();
  const queryClient = useQueryClient();

  const mutation = useMutation(deleteQuotation, {
    onSuccess() {
      notification.success({
        message: intl.formatMessage({
          id: 'quotations.remove.notification.success',
        }),
        description: intl.formatMessage({
          id: 'quotations.remove.notification.successDescription',
        }),
      });

      queryClient.invalidateQueries(['quotations']);
    },
  });

  useServiceError(mutation.error);

  return mutation;
}

export function useSendQuotation(quotationId: number) {
  const queryClient = useQueryClient();
  const intl = useIntl();

  const mutation = useMutation(
    (obj: SendQuotationRequest) => sendQuotation(quotationId, obj),
    {
      onSuccess() {
        notification.success({
          message: intl.formatMessage({
            id: 'quotations.details.sendQuotationRequest.notification.success',
          }),
          description: intl.formatMessage({
            id: 'quotations.details.sendQuotationRequest.notification.successDescription',
          }),
        });

        queryClient.invalidateQueries(['quotations', quotationId]);
      },
      onError(error) {
        notificationError(error, {
          translate: (errors) =>
            errors.map((err) =>
              err.includes('ultracar.errors.service_unavailable.email')
                ? 'Falha ao enviar, coloque as configurações do email.'
                : err,
            ),
        });
      },
    },
  );

  return mutation;
}

export function useQuotationItems(quotationId: number) {
  const query = useQuery(['quotations', quotationId, 'items'], () => {
    return listQuotationItems(quotationId);
  });

  useServiceError(query.error);

  return query;
}

export function useCreateQuotationItem(quotationId: number) {
  const queryClient = useQueryClient();

  const mutation = useMutation(
    (obj: CreateQuotationItemRequest) => createQuotationItem(quotationId, obj),
    {
      async onSuccess(_, data) {
        await queryClient.invalidateQueries([
          'quotations',
          data.quotationId,
          'items',
        ]);
      },
    },
  );

  useServiceError(mutation.error);

  return mutation;
}

export function useUpdateQuotationItem(quotationId: number) {
  const mutation = useMutation((obj: UpdateQuotationItemRequest) =>
    updateQuotationItem(quotationId, obj),
  );

  useServiceError(mutation.error);

  return mutation;
}

export function useDeleteQuotationItem(quotationId: number) {
  const intl = useIntl();
  const queryClient = useQueryClient();

  const mutation = useMutation(
    (id: number) => deleteQuotationItem(quotationId, id),
    {
      onSuccess() {
        notification.success({
          message: intl.formatMessage({
            id: 'quotations.details.items.remove.notification.success',
          }),
          description: intl.formatMessage({
            id: 'quotations.details.items.remove.notification.successDescription',
          }),
        });
        queryClient.invalidateQueries(['quotations', quotationId, 'items']);
      },
    },
  );

  useServiceError(mutation.error);

  return mutation;
}

export function useQuotationSuppliers(quotationId: number, offer?: boolean) {
  const query = useQuery(['quotations', quotationId, 'suppliers'], () => {
    return listQuotationSuppliers(quotationId, offer);
  });

  useServiceError(query.error);

  return query;
}

export function useCreateQuotationSupplier(quotationId: number) {
  const queryClient = useQueryClient();

  const mutation = useMutation(
    (obj: CreateQuotationSupplierRequest) =>
      createQuotationSupplier(quotationId, obj),
    {
      onSuccess() {
        queryClient.invalidateQueries(['quotations', quotationId, 'suppliers']);
      },
    },
  );

  useServiceError(mutation.error);

  return mutation;
}

export function useDeleteQuotationSupplier(quotationId: number) {
  const intl = useIntl();

  const mutation = useMutation(
    (id: number) => deleteQuotationSupplier(quotationId, id),
    {
      onSuccess() {
        notification.success({
          message: intl.formatMessage({
            id: 'quotations.details.suppliers.remove.notification.success',
          }),
          description: intl.formatMessage({
            id: 'quotations.details.suppliers.remove.notification.successDescription',
          }),
        });
      },
    },
  );

  useServiceError(mutation.error);

  return mutation;
}

export function useQuotationReportItemsBySuppliers(
  params: ReportQuotationItemsSuppliersRequest,
) {
  const mutation = useQuery(
    ['reportItemsBySuppliers', { ...params }],
    () => reportQuotationItemsBySyppliers(params),
    {
      enabled: !!params.quotationId && !!params.type,
    },
  );

  useServiceError(mutation.error);

  return mutation;
}

export function useQuotationReportItemsByQuotation(id?: number) {
  const mutation = useQuery(
    ['reportItemsByQuotation', id],
    () => reportQuotationItemsByQuotation(id),
    {
      enabled: !!id,
    },
  );

  useServiceError(mutation.error);

  return mutation;
}

export function useQuotationReportSupplierWinners(id?: number) {
  const mutation = useQuery(
    ['reportSupplierWinners', id],
    () => reportQuotationSupplierWinners(id),
    {
      enabled: !!id,
    },
  );

  useServiceError(mutation.error);

  return mutation;
}

export function useQuotaionItemsBySuppliers() {
  const mutation = useMutation(
    ({
      quotationId,
      type,
    }: {
      quotationId: number;
      type: ReportQuotationType;
    }) => reportQuotationItemsBySyppliers({ quotationId, type }),
  );
  return mutation;
}

export function useItensFromQuotation() {
  const mutation = useMutation(reportQuotationItemsByQuotation);

  return mutation;
}

export function useWinnerQuotation() {
  const mutation = useMutation(reportQuotationSupplierWinners);

  return mutation;
}
